import React, { useMemo, useState } from 'react';
import { AlertTriangle, PackageCheck, Search, Truck } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { EmptyState } from '../ui/empty-state';
import { SearchableSelect } from '../ui/searchable-select';
import { BarrePagination } from '../ui/pagination-bar';
import { usePagination } from '../../hooks/usePagination';
import { cn } from '../../lib/utils';
import { libelle, nomPays, referenceLisible } from '../../lib/libelles';
import { clampAllocation, coveragePercent } from '../../lib/orderProgress';
import { accorde } from '../../lib/formats';
import { ETAT_VARIANTE, jourLisible, libre, nombreEntier, pourcentAffecte } from './outils';

const FILTRE_LIBRES = 'LIBRES';
const FILTRE_TOUS = 'TOUS';

// « Chargement : Dakar » si le trajet le dit, sinon la contenance de la citerne.
// Jamais un code brut: un code pays à deux lettres devient son nom.
const infoCamion = (truck) => {
  const origine = libre(truck.origin, '');
  if (origine) {
    const lieu = /^[A-Za-z]{2}$/.test(origine) ? nomPays(origine, '') : origine;
    if (lieu) return `Chargement : ${lieu}`;
  }
  const citerne = Number(truck.truck_capacity) || 0;
  return citerne > 0 ? `Citerne de ${nombreEntier(citerne)} L` : '';
};

// Chargé ou non: le serveur le dit (`is_loaded`, même règle que le stock).
// Une réponse plus ancienne ne le porte pas: le volume mesuré en tient lieu.
const estCharge = (truck) =>
  typeof truck.is_loaded === 'boolean' ? truck.is_loaded : truck.volume_is_measured !== false;

const Chargement = () => (
  <div className="flex justify-center p-6">
    <span className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
  </div>
);

/**
 * Le détail de la commande choisie et ses actions: une barre affecté/commandé,
 * la couverture, les camions en lignes compactes, et une barre d'actions fixe
 * en bas. Même contenu dans le panneau fixe (grand écran) et dans le volet.
 */
export const DispatchPanel = ({
  order,
  progress,
  orderedLiters,
  allocatedLiters,
  toAllocate,
  hardBlockers,
  trucks,
  trucksLoading,
  quantities,
  setQuantities,
  isSubmitting,
  blockReason,
  basketQuantity,
  basketCount,
  basketOverflow,
  remainingAfterBasket,
  availableCapacity,
  cisternLiters,
  cisternLabel,
  volumeCell,
  uniteCourte,
  suggestedFor,
  onAutoSplit,
  onSubmit,
  onVoirCommandes,
  onVoirTrajets,
  trajetCible = null,
  afficherTitre = true,
}) => {
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState(FILTRE_LIBRES);
  const [filtreLot, setFiltreLot] = useState('');

  const pct = pourcentAffecte(progress);

  const camionsTries = useMemo(
    () =>
      [...trucks].sort(
        (a, b) =>
          // Arrivé depuis un trajet (?trajet=): son camion passe en tête, sans rien cocher.
          Number(b.trip_id === trajetCible) - Number(a.trip_id === trajetCible) ||
          Number(b.available_quantity || 0) - Number(a.available_quantity || 0) ||
          String(a.truck_matricule || '').localeCompare(String(b.truck_matricule || ''), 'fr'),
      ),
    [trucks, trajetCible],
  );
  // Les lots attribués (lot 057) représentés parmi les camions proposés.
  const optionsLots = useMemo(() => {
    const parLot = new Map();
    trucks.forEach((truck) => {
      if (truck.lot_id && truck.lot_reference && !parLot.has(truck.lot_id)) {
        parLot.set(truck.lot_id, { value: truck.lot_id, label: truck.lot_reference });
      }
    });
    return parLot.size > 0 ? [{ value: '', label: 'Tous les lots' }, ...parLot.values()] : [];
  }, [trucks]);
  const lotActif = optionsLots.some((option) => option.value === filtreLot) ? filtreLot : '';

  const camionsAffiches = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return camionsTries.filter((truck) => {
      if (filtre === FILTRE_LIBRES && !(Number(truck.available_quantity) > 0)) return false;
      if (lotActif && truck.lot_id !== lotActif) return false;
      if (!terme) return true;
      return `${truck.truck_matricule || ''} ${truck.driver_name || ''}`.toLowerCase().includes(terme);
    });
  }, [camionsTries, recherche, filtre, lotActif]);
  // Avec des centaines de camions libres, la liste se lit par pages (20, 50, 100).
  // La saisie (cases cochées, quantités) vit au-dessus: elle survit au changement
  // de page, et l'affectation porte sur toutes les pages.
  const pagination = usePagination(camionsAffiches, {
    cle: 'camions',
    reinitialiser: `${recherche}|${filtre}|${lotActif}`,
  });

  // Une phrase de couverture: ce que les camions libres couvrent, et ce qui restera.
  const phraseCouverture = (() => {
    if (toAllocate <= 0 || trucks.length === 0) return null;
    const manque = toAllocate - availableCapacity;
    const camions = accorde(trucks.length, 'camion libre', 'camions libres');
    if (manque > 0) {
      const citernes = Math.round((manque / (cisternLiters || 1)) * 10) / 10;
      const nombre = citernes.toLocaleString('fr-FR');
      return {
        alerte: true,
        texte: `${camions} ${trucks.length > 1 ? 'couvrent' : 'couvre'} ${nombreEntier(
          availableCapacity,
        )} L sur ${nombreEntier(toAllocate)} L — ${nombre} ${cisternLabel}${
          citernes >= 2 ? 's' : ''
        } ${citernes >= 2 ? 'resteront' : 'restera'} à affecter.`,
      };
    }
    return {
      alerte: false,
      texte: `${camions} ${trucks.length > 1 ? 'couvrent' : 'couvre'} tout le reste à affecter (${nombreEntier(
        toAllocate,
      )} L).`,
    };
  })();

  const basculer = (truck) => {
    setQuantities((prev) => {
      if (truck.trip_id in prev) {
        const { [truck.trip_id]: _retire, ...reste } = prev;
        return reste;
      }
      const proposee = suggestedFor(truck);
      return proposee > 0 ? { ...prev, [truck.trip_id]: String(Math.round(proposee)) } : prev;
    });
  };

  const titreAffecter =
    blockReason ||
    (basketCount === 0
      ? 'Cochez au moins un camion, ou saisissez une quantité.'
      : basketOverflow > 0
        ? 'La saisie dépasse le reste à affecter.'
        : undefined);

  const lignes = () => {
    if (trucksLoading) return <Chargement />;
    if (toAllocate <= 0) {
      return (
        <EmptyState
          icon={PackageCheck}
          title="Cette commande est déjà entièrement affectée"
          description="Il ne reste aucun litre à affecter. Étape suivante : suivre le départ des camions, puis enregistrer la livraison dans les commandes."
          actionLabel="Voir les commandes"
          onAction={onVoirCommandes}
          className="border-0 py-8"
        />
      );
    }
    if (trucks.length === 0) {
      return (
        <EmptyState
          icon={Truck}
          title="Aucun camion libre pour le moment"
          description="Un camion apparaît ici dès qu’il est parti charger, qu’il n’est pas archivé et qu’il lui reste du carburant à vendre."
          actionLabel="Voir les trajets"
          onAction={onVoirTrajets}
          className="border-0 py-8"
        />
      );
    }
    if (camionsAffiches.length === 0) {
      return (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Aucun camion ne correspond à cette recherche.
        </p>
      );
    }
    return (
      <ul className="divide-y divide-border rounded-lg border border-border" data-testid="dispatch-trucks">
        {pagination.lignes.map((truck) => {
          const coche = truck.trip_id in quantities;
          const proposee = suggestedFor(truck);
          const valeur = coche ? quantities[truck.trip_id] : proposee > 0 ? String(Math.round(proposee)) : '';
          const part = coveragePercent(truck, toAllocate);
          const info = infoCamion(truck);
          return (
            <li
              key={truck.trip_id}
              data-testid="dispatch-truck-row"
              className={cn('flex items-start gap-3 px-3 py-2.5', coche && 'bg-primary/5')}
            >
              <input
                type="checkbox"
                checked={coche}
                disabled={isSubmitting}
                onChange={() => basculer(truck)}
                aria-label={`Sélectionner le camion ${truck.truck_matricule}`}
                className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary"
              />
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-sm">
                  <span className="font-semibold text-foreground">{truck.truck_matricule}</span>
                  {truck.lot_reference ? (
                    <span
                      className="whitespace-nowrap rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground"
                      data-testid="dispatch-truck-lot"
                    >
                      Lot {truck.lot_reference}
                    </span>
                  ) : null}
                  {trajetCible && truck.trip_id === trajetCible ? (
                    <span
                      className="whitespace-nowrap rounded-md border border-border px-2 py-0.5 text-xs font-semibold text-foreground"
                      data-testid="dispatch-truck-cible"
                    >
                      Ce trajet
                    </span>
                  ) : null}
                  <span className="min-w-0 truncate text-muted-foreground">
                    {libre(truck.driver_name, 'Chauffeur non renseigné')}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {libelle('etape_trajet', truck.trip_status, 'Étape non précisée')}
                  {info ? ` · ${info}` : ''}
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs tabular-nums text-muted-foreground">
                  <span>
                    Disponible : {volumeCell(truck.available_quantity, order)} {uniteCourte}
                  </span>
                  {!estCharge(truck) ? (
                    <Badge
                      variant="muted"
                      className="whitespace-nowrap"
                      title="Pas encore chargé : contenance de la citerne"
                    >
                      non chargé
                    </Badge>
                  ) : truck.volume_is_measured === false ? (
                    <Badge
                      variant="muted"
                      className="whitespace-nowrap"
                      title="Chargé : volume pas encore saisi, estimé d’après le stock"
                    >
                      volume estimé
                    </Badge>
                  ) : null}
                  {part ? <span>Couvre {part} % du reste</span> : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Input
                  type="number"
                  min={0}
                  max={Number(truck.available_quantity) || undefined}
                  inputMode="numeric"
                  aria-label={`Quantité à affecter au camion ${truck.truck_matricule}`}
                  className="h-8 w-24 px-2 text-right text-sm tabular-nums"
                  value={valeur}
                  onChange={(event) => {
                    const saisie = event.target.value;
                    setQuantities((prev) => ({ ...prev, [truck.trip_id]: saisie }));
                  }}
                  // Ramenée dans les limites en quittant la case, pas à chaque
                  // frappe: taper « 12000 » ne doit pas être coupé à « 1 ».
                  onBlur={() => {
                    const saisie = quantities[truck.trip_id];
                    if (saisie === undefined || saisie === '') return;
                    const autres = basketQuantity - Math.max(0, Number(saisie) || 0);
                    const limitee = clampAllocation(
                      saisie,
                      truck.available_quantity,
                      Math.max(0, toAllocate - autres),
                    );
                    setQuantities((prev) => ({
                      ...prev,
                      [truck.trip_id]: limitee > 0 ? String(Math.round(limitee)) : '',
                    }));
                  }}
                />
                <span className="text-xs text-muted-foreground">L</span>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="dispatch-panel">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {afficherTitre && (
              <h2 className="text-base font-semibold text-foreground">
                Commande {referenceLisible(order.reference_number)}
              </h2>
            )}
            <p className="text-sm text-muted-foreground">
              {libre(order.client_name, 'Client non renseigné')} ·{' '}
              {libre(order.delivery_location, 'Destination non précisée')} · livraison demandée :{' '}
              {jourLisible(order.requested_delivery_date)}
            </p>
          </div>
          <Badge variant={ETAT_VARIANTE[progress.state] || 'muted'} className="whitespace-nowrap">
            {libelle('etat_commande', progress.state)}
          </Badge>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="font-medium text-foreground">
              Affecté {nombreEntier(allocatedLiters)} L sur {nombreEntier(orderedLiters)} L
            </span>
            <span className="tabular-nums text-muted-foreground">{pct} %</span>
          </div>
          <div
            className="h-2 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label="Part affectée de la commande"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
          >
            <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
          <p className="flex flex-wrap gap-x-3 text-xs tabular-nums text-muted-foreground">
            <span className={remainingAfterBasket > 0 ? 'font-semibold text-flame-ink' : undefined}>
              Reste à affecter : {nombreEntier(remainingAfterBasket)} L
            </span>
            {progress.delivered_liters > 0 ? (
              <span>{nombreEntier(progress.delivered_liters)} L déjà livrés</span>
            ) : null}
            {basketQuantity > 0 ? (
              <span>dont {nombreEntier(basketQuantity)} L saisis, pas encore validés</span>
            ) : null}
            {basketOverflow > 0 ? (
              <span className="font-semibold text-destructive">
                {nombreEntier(basketOverflow)} L en trop dans la saisie
              </span>
            ) : null}
          </p>
        </div>

        {/* Ce qui empêche vraiment d'affecter, dit en toutes lettres. */}
        {hardBlockers.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm">
            <p className="mb-1 font-medium text-foreground">Ce qui empêche d’affecter</p>
            {hardBlockers.map((reason) => (
              <div key={reason.message} className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                <span>{reason.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Une flotte qui ne couvre qu'une partie n'est pas un blocage: on
            affecte ce qu'on peut, le reste attendra un autre camion. */}
        {phraseCouverture && hardBlockers.length === 0 && (
          <div
            className={cn(
              'flex gap-2 rounded-lg border p-3 text-sm',
              phraseCouverture.alerte
                ? 'border-flame/30 bg-flame/10'
                : 'border-border bg-muted/40',
            )}
          >
            <AlertTriangle
              className={cn(
                'mt-0.5 h-4 w-4 shrink-0',
                phraseCouverture.alerte ? 'text-flame-ink' : 'text-muted-foreground',
              )}
              aria-hidden="true"
            />
            <p className="text-foreground">{phraseCouverture.texte}</p>
          </div>
        )}

        {trucks.length > 0 && toAllocate > 0 && !trucksLoading && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 basis-40">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={recherche}
                onChange={(event) => setRecherche(event.target.value)}
                placeholder="Plaque ou chauffeur"
                aria-label="Rechercher un camion"
                className="h-8 pl-8 text-sm"
              />
            </div>
            {optionsLots.length > 0 ? (
              <SearchableSelect
                className="h-8 w-40 text-sm"
                data-testid="dispatch-filtre-lot"
                aria-label="Lot"
                placeholder="Lot"
                searchPlaceholder="Rechercher un lot…"
                emptyMessage="Aucun lot trouvé"
                value={lotActif}
                onValueChange={setFiltreLot}
                options={optionsLots}
              />
            ) : null}
            <div className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
              {[
                [FILTRE_LIBRES, 'Libres'],
                [FILTRE_TOUS, 'Tous'],
              ].map(([valeur, texte]) => (
                <button
                  key={valeur}
                  type="button"
                  onClick={() => setFiltre(valeur)}
                  aria-pressed={filtre === valeur}
                  className={cn(
                    'h-7 rounded-md px-2.5 text-xs font-semibold transition-colors',
                    filtre === valeur
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {texte}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">Camions libres</h3>
          {lignes()}
          {!trucksLoading && toAllocate > 0 && camionsAffiches.length > 0 ? (
            <BarrePagination pagination={pagination} nom="camions" />
          ) : null}
        </div>
      </div>

      {/* Barre d'actions fixe: toujours à portée, quelle que soit la longueur de la liste. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-card px-4 py-3">
        <Button
          variant="outline"
          size="sm"
          onClick={onAutoSplit}
          disabled={isSubmitting || trucks.length === 0 || toAllocate <= 0}
          title={
            trucks.length === 0
              ? 'Aucun camion libre pour le moment.'
              : toAllocate <= 0
                ? 'Cette commande est déjà entièrement affectée.'
                : 'Remplit les quantités, du plus gros volume au plus petit.'
          }
        >
          Répartir automatiquement
        </Button>
        <span title={titreAffecter} className="min-w-0">
          <Button
            size="sm"
            disabled={isSubmitting || basketCount === 0 || basketOverflow > 0 || Boolean(blockReason)}
            onClick={onSubmit}
          >
            {isSubmitting
              ? 'Envoi en cours…'
              : `Affecter ${basketCount} camion${basketCount > 1 ? 's' : ''} · ${nombreEntier(basketQuantity)} L`}
          </Button>
        </span>
      </div>
    </div>
  );
};
